import shp from 'shpjs';
import JSZip from 'jszip';

/** ZIP 안의 레이어별 구성 확인 */
export async function validateShapefileZip(file) {
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const names = Object.keys(zip.files).map(n => n.toLowerCase());

    // baseName 묶기 (확장자 분리)
    const groups = new Map(); // base -> Set(ext)
    for (const n of names) {
        const m = n.match(/(.+)\.(shp|shx|dbf|prj|cpg)$/);
        if (!m) continue;
        const base = m[1];
        const ext = m[2];
        if (!groups.has(base)) groups.set(base, new Set());
        groups.get(base).add(ext);
    }

    const layers = [];
    for (const [base, exts] of groups.entries()) {
        layers.push({
            base,
            filesPresent: Array.from(exts),
            hasSHP: exts.has('shp'),
            hasSHX: exts.has('shx'),
            hasDBF: exts.has('dbf'),
            hasPRJ: exts.has('prj'),
            hasCPG: exts.has('cpg'),
            isComplete: exts.has('shp') && exts.has('shx') && exts.has('dbf'),
        });
    }

    const complete = layers.filter(l => l.isComplete);
    const errors = [];
    if (layers.length === 0) {
        errors.push('ZIP 안에서 SHP/SHX/DBF 파일 세트를 찾지 못했습니다.');
    } else if (complete.length === 0) {
        errors.push('SHP 세트를 찾았지만 필수 파일(shp/shx/dbf)이 하나 이상 누락되었습니다.');
    }

    return {
        ok: complete.length > 0,
        layers,
        best: complete[0] || null, // 가장 첫 완전 레이어
        warnings: layers
            .filter(l => l.isComplete && !l.hasPRJ)
            .map(l => `${l.base} : .prj가 없어 좌표계가 불명확할 수 있습니다.`),
        errors,
    };
}

/** ZIP → GeoJSON (가장 완전한 레이어 우선) */
export async function parseShapefileZip(file) {
    // shpjs는 zip ArrayBuffer 전체를 파싱
    const ab = await file.arrayBuffer();
    const res = await shp(ab);
    // 여러 레이어일 경우 배열/object로 올 수 있으므로 정규화
    if (res?.type === 'FeatureCollection') return res;
    if (res?.type === 'Feature') return { type: 'FeatureCollection', features: [res] };
    if (Array.isArray(res)) {
        const fc = res.find(r => r?.type === 'FeatureCollection');
        if (fc) return fc;
    }
    if (res && typeof res === 'object') {
        // shpjs가 {layerName: FC, ...} 형태를 줄 수도 있음
        const fc = Object.values(res).find(v => v?.type === 'FeatureCollection');
        if (fc) return fc;
    }
    return { type: 'FeatureCollection', features: [] };
}

export function geojsonToGrid(geojson) {
    const feats = geojson?.features || [];
    const headersSet = new Set();
    feats.forEach(f => Object.keys(f.properties || {}).forEach(k => headersSet.add(k)));
    const headers = Array.from(headersSet);

    const columns = headers.map((h, idx) => ({
        name: h, header: h, value: h, idx, width: 240, sortable: true, filter: 'text', editable: false,
    }));

    const rows = feats.map((f, i) => {
        const obj = { id: i + 1 };
        headers.forEach(h => obj[h] = f.properties?.[h] ?? '');
        return obj;
    });

    return { columns, rows, headers };
}

export function makeCsvBlobFromRows(rows, headers) {
    const esc = (v) => {
        const s = String(v ?? '');
        if (s.includes('"') || s.includes(',') || /[\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
    };
    const lines = [headers.join(',')];
    for (const r of rows) lines.push(headers.map(h => esc(r[h])).join(','));
    return new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
}

// PRJ 텍스트 읽기
export async function readPrjFromZip(file) {
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const prjEntry = Object.keys(zip.files).find(n => /\.prj$/i.test(n));
    if (!prjEntry) return null;
    return await zip.files[prjEntry].async('string');
}

// PRJ 텍스트 기반 간이 EPSG 추정
export function detectEpsgFromPrj(prjText) {
    if (!prjText) return null;
    const t = prjText.toLowerCase();
    // 아주 러프한 매핑 (필요시 더 추가)
    if (t.includes('wgs_1984') || t.includes('gcs_wgs_1984') || t.includes('wgs 84')) return 'EPSG:4326';
    if (t.includes('korea 2000') && t.includes('central belt 2010')) return 'EPSG:5186';
    if (t.includes('korean datum 1985') || t.includes('korean_1985')) return 'EPSG:5174';
    if (t.includes('wgs_1984_utm_zone_52n')) return 'EPSG:32652';
    if (t.includes('wgs_1984_utm_zone_51n')) return 'EPSG:32651';
    return null; // 모르면 null
}
